import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
// --- external imports ------

import AuthLoadingScreen from '~/authentication/AuthLoadingScreen';
import firebase from '~/authentication/lib/firebase';
import { markLinkActionSpent } from '~/authentication/lib/pendingLinkAction';
import { clearUnusableSignIn } from '~/authentication/lib/unusableSignIn';
import { clearSentLinkNotice } from '~/authentication/lib/sentLinkNotice';
import { useUserContext } from '~/authentication/UserProvider';
import InvalidLink from '~/authentication/InvalidLink';
import AuthErrorScreen, { useAuthErrorScreen } from '~/authentication/AuthErrorScreen';
// --- internal imports ------

// The code in the link is spent by the first exchange. A second call for the same
// code loses the race and rejects with `EMAIL_EXISTS`, even though the first one
// signed the player in — and that rejection is what puts the invalid-link screen
// up over a working session. React can run the effect below more than once for a
// single visit (a StrictMode remount, or a router that rebuilds), so the exchange
// is keyed on the code itself and shared across mounts rather than guarded by a
// ref, which a remount would discard.
const exchangesByCode = new Map();

function exchangeEmailLink(email, href, oobCode, adoptSignedInUser) {
	const key = oobCode || href;

	if (!exchangesByCode.has(key)) {
		const exchange = firebase.signInWithEmailLink(email, href).then(async (credential) => {
			// Both records describe the sign-in this one replaces, and both are
			// cleared here rather than before the call: a refused exchange leaves
			// the old session standing as the live one, and clearing them on the
			// way in would have handed that same stale session the registration
			// form again for one more refused submit, and the recovery screen its
			// request form again for one more needless send.
			//
			// The sign-in that could not set a password. A new link is the fresh
			// sign-in that record exists to obtain, so spending one takes it with
			// it — but only once spending it has actually worked.
			clearUnusableSignIn();

			// The link the confirmation promised is this one, now spent. Whatever
			// it was still saying about mail in flight has been overtaken by its
			// arrival.
			clearSentLinkNotice();

			// The fresh session is handed to the provider rather than waited for
			// from Firebase. Firebase notifies its auth-state listeners only when
			// the uid changes, and this is the same account signing in again — so
			// `onAuthStateChanged` may never fire, and `UserProvider` would keep
			// the user object whose `auth_time` is the one just refused.
			// `adoptSignedInUser` is idempotent, so the notification arriving
			// anyway costs nothing.
			//
			// Awaited so the route below is released only once the login has
			// finished, rather than woken to find half a session.
			//
			// Absent only where this screen is rendered outside the provider, which
			// has no session for the sign-in to land on either way.
			await adoptSignedInUser?.(credential?.user);

			return credential;
		})
			// Either way, and last. The route is holding its redirect for this code,
			// so nothing may release it until the attempt is over — and a refusal
			// has to release it too, or a dead code would pin a signed-in player to
			// the recovery screen whose only exit now ends their session.
			.finally(() => markLinkActionSpent(oobCode));

		exchangesByCode.set(key, exchange);
	}

	return exchangesByCode.get(key);
}

function EmailLoginRedirector() {
	const history = useNavigate();
	const { adoptSignedInUser } = useUserContext();
	const [isLoading, setIsLoading] = useState(true);

	useEffect(handleOnMount, [history, adoptSignedInUser]);

	const releaseLoadingState = useCallback(() => {
		setIsLoading(false);
	}, []);

	// The second half of the flow belongs to `UserProvider`: once the link is
	// exchanged for a Firebase session, it logs the player into the user system.
	// A failure there is reported on the context and never on the call below, so
	// without reading it this screen holds its cursor for a request that has
	// already failed. A refused session is what an expired link looks like, so
	// that one takes the invalid-link screen — the hook leaves no screen of its
	// own up for it, which is the fall-through below.
	const {
		screenError,
		clearScreenError,
		onRetry,
		isRetrying
	} = useAuthErrorScreen({ onSettled: releaseLoadingState });

	const handleBackToLogin = useCallback(() => {
		clearScreenError();
		history('/login');
	}, [clearScreenError, history]);

	function handleOnMount() {
		const queryString = window.location.search;
		const urlParams = new URLSearchParams(queryString);

		const oobCode = urlParams.get('oobCode');

		const email = urlParams.get('email');
		if (email === null) {
			console.error('No email provided with link!');
			// Nothing here will be spent, so the route is released at once. Held,
			// the gate would keep a signed-in player on this screen over a link
			// that was never going to work.
			markLinkActionSpent(oobCode);
			setIsLoading(false);
			return;
		}

		exchangeEmailLink(email, window.location.href, oobCode, adoptSignedInUser)
			.catch((error) => {
				console.error(error);
				setIsLoading(false);
			});
	}

	// The email link is being exchanged for a session, and the provider then logs
	// the player in — the screen stays loading for both halves of that flow.
	if (isLoading) {
		return <AuthLoadingScreen isBusy />;
	}

	if (screenError) {
		return (
			<AuthErrorScreen
				error={screenError}
				onRetry={onRetry}
				onBack={handleBackToLogin}
				isRetrying={isRetrying}
			/>
		);
	}

	return <InvalidLink />;
};

export default EmailLoginRedirector;
